import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import type { evereeTimesheet } from "types/api-types/payroll/everee-timesheets/everee-timesheets.types";
import type { evereeWorkerData } from "types/api-types/payroll/everee-worker-data/everee-worker-data.types";

export type TimesheetStatus = "PENDING" | "APPROVED" | "PAID" | "REJECTED" | "";

/**
 * EvereeTimesheetManagementStore - Manages timesheet data for admin interface
 */
class EvereeTimesheetManagementStore {
  rootStore: RootStore;

  // Observable Properties
  isLoading = false;
  error: string | null = null;
  statusFilter: TimesheetStatus = "";
  workerIdFilter = "";
  startDate: string = "";
  endDate: string = "";
  selectedTimesheetId: string | null = null;

  // DataGrid state - uses DataGridColumnHeader format (key, title, width)
  currentHeaders: Array<{ key: string; title: string; width: number }> = [
    { key: "shiftId", title: "Shift ID", width: 120 },
    { key: "workerName", title: "Worker", width: 180 },
    { key: "clockIn", title: "Clock In", width: 160 },
    { key: "clockOut", title: "Clock Out", width: 160 },
    { key: "hours", title: "Hours", width: 80 },
    { key: "status", title: "Status", width: 100 },
    { key: "workLocationId", title: "Location", width: 120 },
  ];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ServiceStore accessors
  get evereeTimesheetsStore(): ServiceStore {
    return this.rootStore.evereeTimesheetsStore as ServiceStore;
  }

  get evereeWorkerDataStore(): ServiceStore {
    return this.rootStore.evereeWorkerDataStore as ServiceStore;
  }

  get evereeWorkLocationsStore(): ServiceStore {
    return this.rootStore.evereeWorkLocationsStore as ServiceStore;
  }

  // Computed Values
  get timesheets(): evereeTimesheet[] {
    return (this.evereeTimesheetsStore?.entities || []) as unknown as evereeTimesheet[];
  }

  get workers(): evereeWorkerData[] {
    return (this.evereeWorkerDataStore?.entities || []) as unknown as evereeWorkerData[];
  }

  get workerMap(): Map<string, string> {
    const map = new Map<string, string>();
    this.workers.forEach((w) => {
      map.set(w.workerId, `${w.firstName} ${w.lastName}`);
    });
    return map;
  }

  get filteredTimesheets(): evereeTimesheet[] {
    let result = this.timesheets;

    if (this.statusFilter) {
      result = result.filter((t) => t.status === this.statusFilter);
    }

    if (this.workerIdFilter) {
      result = result.filter((t) => t.workerId === this.workerIdFilter);
    }

    if (this.startDate) {
      const startEpoch = new Date(this.startDate).getTime() / 1000;
      result = result.filter((t) => (t.shiftStartEpochSeconds ?? 0) >= startEpoch);
    }

    if (this.endDate) {
      const endEpoch = new Date(this.endDate + "T23:59:59").getTime() / 1000;
      result = result.filter((t) => (t.shiftStartEpochSeconds ?? 0) <= endEpoch);
    }

    return result;
  }

  get dataGridData(): Record<string, unknown>[] {
    return this.filteredTimesheets.map((t) => {
      const shiftId = t.workedShiftId || t.shiftId || "";
      return {
        id: shiftId,
        shiftId,
        workerId: t.workerId,
        workerName: this.workerMap.get(t.workerId) || t.workerId,
        clockIn: t.shiftStartEpochSeconds
          ? new Date(t.shiftStartEpochSeconds * 1000).toLocaleString()
          : "",
        clockOut: t.shiftEndEpochSeconds
          ? new Date(t.shiftEndEpochSeconds * 1000).toLocaleString()
          : "",
        hours: t.hours?.toFixed(2) || "",
        status: t.status || "",
        workLocationId: t.workLocationId || "",
      };
    });
  }

  get totalCount(): number {
    return this.timesheets.length;
  }

  get filteredCount(): number {
    return this.filteredTimesheets.length;
  }

  // Actions
  setStatusFilter = (status: TimesheetStatus) => {
    this.statusFilter = status;
  };

  setWorkerIdFilter = (workerId: string) => {
    this.workerIdFilter = workerId;
  };

  setDateRange = (start: string, end: string) => {
    this.startDate = start;
    this.endDate = end;
  };

  setSelectedTimesheetId = (shiftId: string | null) => {
    this.selectedTimesheetId = shiftId;
  };

  clearFilters = () => {
    this.statusFilter = "";
    this.workerIdFilter = "";
    this.startDate = "";
    this.endDate = "";
  };

  // Async Actions
  loadTimesheets = async (companyId?: string) => {
    this.isLoading = true;
    this.error = null;

    try {
      // Build query with company filter if provided
      const timesheetsQuery: Record<string, unknown> = { $limit: 1000 };
      const workersQuery: Record<string, unknown> = { page: 0, size: 100 };

      if (companyId) {
        timesheetsQuery.companyId = companyId;
        workersQuery.companyId = companyId;
      }

      await Promise.all([
        this.evereeTimesheetsStore.find({ query: timesheetsQuery }),
        // Use everee-worker-data for listing workers (everee-workers only supports create/remove)
        this.evereeWorkerDataStore.find({ query: workersQuery }),
      ]);
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to load timesheets";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  createTimesheet = async (data: Partial<evereeTimesheet>): Promise<boolean> => {
    try {
      await this.evereeTimesheetsStore.create(data);
      await this.loadTimesheets();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to create timesheet";
      });
      return false;
    }
  };

  updateTimesheet = async (
    shiftId: string,
    data: Partial<evereeTimesheet>
  ): Promise<boolean> => {
    try {
      await this.evereeTimesheetsStore.patch(shiftId, data);
      await this.loadTimesheets();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to update timesheet";
      });
      return false;
    }
  };

  approveTimesheet = async (shiftId: string): Promise<boolean> => {
    return this.updateTimesheet(shiftId, { status: "APPROVED" });
  };

  rejectTimesheet = async (shiftId: string): Promise<boolean> => {
    return this.updateTimesheet(shiftId, { status: "REJECTED" });
  };

  deleteTimesheet = async (shiftId: string): Promise<boolean> => {
    try {
      await this.evereeTimesheetsStore.remove(shiftId);
      await this.loadTimesheets();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to delete timesheet";
      });
      return false;
    }
  };

  reset = () => {
    this.isLoading = false;
    this.error = null;
    this.statusFilter = "";
    this.workerIdFilter = "";
    this.startDate = "";
    this.endDate = "";
    this.selectedTimesheetId = null;
  };
}

export default EvereeTimesheetManagementStore;
